import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * useCompanyTechnicians
 *
 * Subscribes to `/companies/{companyId}/users` and returns the subset of
 * members whose `role === 'technician'`. This is the **canonical source** for
 * the Dispatch board's technician columns — invited/accepted technicians land
 * in the membership subcollection (see iter_158 fix) and must appear as a
 * dispatchable column even when they have zero jobs assigned yet.
 *
 * Returns:
 *   - technicians: Array<{ uid, name, email, role }>
 *   - loading: boolean
 *   - error: Error | null
 */
export function useCompanyTechnicians(companyId) {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    if (!companyId || !db) {
      setTechnicians([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const ref = collection(db, 'companies', companyId, 'users');

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const out = [];
        snap.forEach((doc) => {
          const data = doc.data() || {};
          if ((data.role || '').toLowerCase() !== 'technician') return;
          if (data.status && data.status !== 'active') return; // respect explicit inactive
          const email = (data.email || '').trim();
          const name  = (data.name || '').trim() || (email ? email.split('@')[0] : doc.id);
          out.push({
            uid: doc.id,
            name,
            email,
            role: 'technician',
            // Persistent dispatch defaults — read-only fields, missing means
            // "no default set" and the Build Day sheet falls back to the
            // company depot / hard-coded fallback chain.
            defaultStartAddress: (data.defaultStartAddress || '').trim(),
            defaultMaxStops: typeof data.defaultMaxStops === 'number'
              ? data.defaultMaxStops
              : null,
            defaultHalfDay: typeof data.defaultHalfDay === 'boolean'
              ? data.defaultHalfDay
              : null,
            // Phase 1 end-location fields (iter_225). Missing → null so the
            // UI can show "Use company default" instead of leaking empty
            // strings into payloads.
            defaultHomeAddress: (data.defaultHomeAddress || '').trim(),
            defaultEndAddress:  (data.defaultEndAddress  || '').trim(),
            endLocationMode:    (data.endLocationMode    || '').toLowerCase() || null,
          });
        });
        // Stable alpha sort by display name so columns don't reshuffle between renders.
        out.sort((a, b) => a.name.localeCompare(b.name));
        setTechnicians(out);
        setLoading(false);
      },
      (err) => {
        console.error('[useCompanyTechnicians] snapshot error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [companyId]);

  return { technicians, loading, error };
}

export default useCompanyTechnicians;
